import React, { forwardRef, ChangeEvent, FocusEvent } from 'react';

import { FieldRenderProps } from 'react-final-form';
import styled from '@emotion/styled';

import FieldWrapper from '../field-wrapper/field-wrapper';
import Box from '../../box/box';
import Icon, { IconTypes } from '../../icon/icon';

export type InputTagsType = 'input' | 'textarea';

interface StyledInputProps {
  /** Checks if the field has an error message. */
  hasError?: boolean;
  /** Checks if we should display an Icon in the input field. */
  hasIcon?: boolean;
}

export interface InputProps extends FieldRenderProps<string, HTMLInputElement> {
  /** The available HTML tags for the input. */
  as?: InputTagsType;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Checks if we should display an Icon in the input field. */
  hasIcon?: boolean;
  /** The specific icon type to be displayed. */
  iconType?: IconTypes;
  /** Checks if the field is disabled. */
  isDisabled?: boolean;
  /** Checks if the field is hidden. */
  isHidden?: boolean;
  /** Checks if the field is required to submit. */
  isRequired?: boolean;
}

const StyledInput = styled.input<StyledInputProps>(({ hasError, hasIcon, disabled, theme, required }) => [
  {
    label: 'input',
    width: '100%',
    padding: theme.space.s16,
    borderRadius: theme.radii.form,
    border: `1px solid ${theme.colors.coolGrey[80]}`,
    marginTop: theme.space.s8,
    outline: 'none',
    '&:focus': {
      border: `2px solid ${theme.colors.coolGrey[100]}`,
    },
    '&::placeholder': {
      color: theme.colors.coolGrey[60],
    },
  },
  !hasError && {
    '&:not(&:placeholder-shown)': {
      border: `1px solid ${theme.colors.coolGrey[80]}`,
    },
  },
  required &&
    hasError && {
      border: `1px solid ${theme.colors.pink[60]}`,
    },
  disabled && {
    border: `1px solid ${theme.colors.coolGrey[20]}`,
    '&:active': {
      border: `1px solid ${theme.colors.coolGrey[20]}`,
    },
  },
  hasIcon && {
    paddingLeft: theme.space.s48,
  },
]);

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      customErrorMessage,
      hasIcon = false,
      iconType = 'MagnifyingGlass',
      id,
      input,
      isDisabled = false,
      isFullWidth = false,
      isHidden = false,
      isRequired = false,
      label,
      meta,
      placeholder = 'placeholder',
      type,
      ...rest
    },
    forwardedRef,
  ) => {
    const hasError = meta?.error && meta?.touched;
    const errorMessage = customErrorMessage || meta?.error;
    const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
      if (input.onBlur) {
        input.onBlur(e);
      }

      return e.target.value;
    };

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (input.onChange) {
        input.onChange(e);
      }

      return e.target.value;
    };

    const handleOnFocus = (e: FocusEvent<HTMLInputElement>) => {
      if (input.onFocus) {
        input.onFocus(e);
      }

      return e.target.value;
    };

    return (
      <FieldWrapper
        className={className}
        customErrorMessage={errorMessage}
        hasError={hasError}
        id={id}
        isDisabled={isDisabled}
        isFullWidth={isFullWidth}
        isHidden={isHidden}
        isRequired={isRequired}
        label={label}
      >
        <Box
          css={[
            {
              label: 'inputContainer',
              width: 'inherit',
            },
            hasIcon && {
              position: 'relative',
            },
          ]}
        >
          {hasIcon && (
            <Icon
              color="coolGrey.60"
              css={{
                height: '20px',
                width: '20px',
                position: 'absolute',
                transform: 'translate(16px, -50%)',
                top: 'calc(50% + 4px)',
              }}
              type={iconType}
            />
          )}
          <StyledInput
            {...input}
            {...rest}
            ref={forwardedRef}
            aria-describedby={hasError ? `errorMessage-${id}` : undefined}
            data-testid={`formInput-${id}`}
            disabled={isDisabled}
            hasError={hasError}
            hasIcon={hasIcon}
            id={id}
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            onFocus={handleOnFocus}
            placeholder={placeholder}
            required={isRequired}
          />
        </Box>
      </FieldWrapper>
    );
  },
);

export { Input };
export default Input;
